import React from 'react';
import { getTranslationCommon, type LANGUAGE } from '@nfctron/nexus-core';

import { useContextRequired } from '../hooks';

/**
 * Interface for the internationalization context properties
 */
interface IntlContextProps {
	/**
	 * Current language identifier
	 */
	languageId: LANGUAGE;
	/**
	 * Function to update the current language
	 * @param languageId - The new language identifier to set
	 */
	setLanguageId: (languageId: LANGUAGE) => void;
}

const IntlContext = React.createContext<IntlContextProps | undefined>(undefined);
IntlContext.displayName = 'IntlContext';

/**
 * Internationalization provider component that gives access to the current language.
 * Required to use any i18n sensitive components from @nfctron/nexus-react.
 *
 * @returns IntlProvider component
 */
export const IntlProvider: React.FC<React.PropsWithChildren<IntlContextProps>> = ({ children, languageId, setLanguageId }) => (
	<IntlContext.Provider value={{ languageId, setLanguageId }}>{children}</IntlContext.Provider>
);

/**
 * Hook to access the internationalization context.
 * Provides access to the current language and language setter.
 *
 * @returns The IntlContext value with languageId and setLanguageId
 * @throws Error if used outside of IntlProvider
 */
export const useIntl = () => useContextRequired(IntlContext);

/**
 * Hook that provides access to common translations.
 *
 * @returns A translation function for common application texts
 */
export const useTranslationsCommon = () => {
	const { languageId } = useIntl();
	return getTranslationCommon(languageId);
};
